import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import usePermission from "../../usePermission";
import { TrackNowButton } from "../ui/TrackNowButton";
import { useRef } from "react";
import { useCommonDetailsContext } from "../../context/Common/common";
import { tokens } from "../../theme/tokens";


const StyledPopover = styled("div")(() => ({
  ".fc.fc-theme-standard .fc-view-harness .fc-popover": {
    zIndex: 1,
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover": {
    width: "var(--fc-more-popover-width, auto)",
    maxWidth: "var(--fc-more-popover-width, 220px)",
    minWidth: 0,
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-popover-body": {
    minWidth: 0,
    maxHeight: "var(--fc-more-popover-max-height, none)",
    overflowY: "var(--fc-more-popover-overflow-y, visible)",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    scrollbarWidth: "thin",
    scrollbarColor: "#9f9f9f #ececec",
    padding: "8px",
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-popover-body::-webkit-scrollbar": {
    width: "10px",
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-popover-body::-webkit-scrollbar-track": {
    background: "#ececec",
    borderRadius: "10px",
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-popover-body::-webkit-scrollbar-thumb": {
    background: "#9f9f9f",
    borderRadius: "10px",
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-daygrid-event-harness, .fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-daygrid-event-harness-abs": {
    maxWidth: "100%",
  },
  ".fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-event-main, .fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-event-main-frame, .fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-event-title, .fc.fc-theme-standard .fc-view-harness .fc-more-popover .fc-event-title-container": {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const WhiteCheckbox = styled(Checkbox)(({ theme }) => ({
  color: "white",
  "&.Mui-checked": {
    color: "white",
  },
  width: "25px",
  height: "25px",
}));

const Calendar = (props) => {
  const { publishedBranding, clientScopedBranding, validateParentData } = useCommonDetailsContext();
  const activeBranding = validateParentData?.isSingleParent ? publishedBranding : (clientScopedBranding || publishedBranding);
  const primaryColor = activeBranding?.primaryColor || tokens.colors.connectedPurple;
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  let { clientId } = useParams();
  const [calendarEventList, setCalendarEventList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedeventDisplay, setSelectedEventDisplay] = useState({});

  const handleEventClick = (filing) => {
    console.log("handleEventClick filing", filing);
    props.setSelectedEventDisplay(filing.event);
  };

  // const handleDateChange = (filing) => {
  //   props.onDateSetsChange(filing);
  // };

  const handleDateChange = (arg) => {
    // This gives the actual start of the current calendar view (e.g., Jan 1st for January)
    const currentDate = new Date(arg.view.currentStart);
    const currentYear = currentDate.getFullYear();

    if (props.onYearChange) {
      props.onYearChange(currentYear);
    }

    props.onDateSetsChange(arg);
  };

  const handleCheckboxChange = (filing, e) => {
    props.setSelectedEvent(filing.event, e);
  };

  const hasChangeDueDatePermission = usePermission(
    "ClientFilingList",
    "changeStatusAndDueDate",
    clientId
  );

  const handleDragAndDropEvent = (filing) => {
    const { recordType, isMasterFiling } = filing.event.extendedProps;
    if (
      !hasChangeDueDatePermission ||
      (recordType === "TrackNow" && isMasterFiling)
    ) {
      filing.revert();
    } else {
      props.dragAndDropEvent(filing.event, filing.oldEvent);
    }
  };

  const handleEventDragStart = (info) => {
    // console.log("handleEventDragStart", info.event.extendedProps);
    const { recordType, isMasterFiling } = info.event.extendedProps;
    if (
      !hasChangeDueDatePermission ||
      (recordType === "TrackNow" && isMasterFiling)
    ) {
      info.jsEvent.preventDefault();
      return;
    }

    // When dragging from the "+n more" popover, close it so hidden day cells
    // can receive the drop underneath the popover.
    document
      .querySelectorAll(".fc-popover")
      .forEach((popover) => popover.remove());
  };

  const renderEventContent = (filing) => {
    if (filing.event.id) {
      const isSelected = selectedeventDisplay?.event?.id === filing.event.id;
      const isChecked = selectedEvent.some(
        (event) => event.id === filing.event.id
      );

      const checkPastDueDate = () => {
        const filingDueDate = new Date(
          filing.event._def.extendedProps.filingDueDate
        ); // Parse the filingDueDate

        const currentDate = new Date(); // Get the current date
        filingDueDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        // Compare filingDueDate with currentDate
        return filingDueDate < currentDate;
      };

      // Simplified borderColor logic
      const status = filing.event._def.extendedProps.status;
      const isPastDueDate = checkPastDueDate();
      let borderColor = "";
      const isClientFiling = filing.event._def.extendedProps.isMasterFiling
        ? false
        : true;

      if (status === "Completed") {
        borderColor = "#28a745"; // Green for all dates with status Completed
      } else if (isPastDueDate) {
        borderColor = "#dc3545"; // Red for past due dates, excluding Completed status
      }

      return (
        <Tooltip title={filing.event.title}>
          <div
            style={{
              border: isSelected ? "3px solid #46dce5" : "none",
              borderRadius: "4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: borderColor,
              overflow: "hidden", // Ensure no overflow
              whiteSpace: "nowrap", // Prevent wrapping
              textOverflow: "ellipsis", // Truncate text with ellipsis
            }}
          >
            <WhiteCheckbox
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(filing, e)}
              size="small"
            />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: isClientFiling ? "bold" : "normal",
                fontStyle: isClientFiling ? "italic" : "normal",
              }}
            >
              {filing.event.title}
            </span>
          </div>
        </Tooltip>
      );
    }
  };
  const handleTodayClick = () => {
    console.log("handleTodayClick calendarRef", calendarRef.current);
  };

  const handleMoreLinkClick = (info) => {
    const clickedCell = info.jsEvent?.currentTarget?.closest(".fc-daygrid-day");
    const cellFrame =
      clickedCell?.querySelector(".fc-daygrid-day-frame") || clickedCell;
    const cellWidth = cellFrame?.getBoundingClientRect?.().width;

    if (calendarContainerRef.current) {
      if (cellWidth) {
        calendarContainerRef.current.style.setProperty(
          "--fc-more-popover-width",
          `${Math.max(Math.round(cellWidth * 1.25), 120)}px`
        );
      } else {
        calendarContainerRef.current.style.removeProperty(
          "--fc-more-popover-width"
        );
      }

      if (info.hiddenSegs?.length > 2) {
        calendarContainerRef.current.style.setProperty(
          "--fc-more-popover-max-height",
          "150px"
        );
        calendarContainerRef.current.style.setProperty(
          "--fc-more-popover-overflow-y",
          "scroll"
        );
      } else {
        calendarContainerRef.current.style.removeProperty(
          "--fc-more-popover-max-height"
        );
        calendarContainerRef.current.style.removeProperty(
          "--fc-more-popover-overflow-y"
        );
      }
    }

    return "popover";
  };

  const calendarConfig = {
    aspectRatio: 1.4,
    plugins: [dayGridPlugin, interactionPlugin, bootstrap5Plugin],
    initialView: "dayGridMonth",
    events: calendarEventList,
    dayMaxEvents: true,
    editable: true,
    eventContent: renderEventContent,
    eventClick: handleEventClick,
    datesSet: handleDateChange,
    eventDrop: handleDragAndDropEvent,
    eventResize: handleDragAndDropEvent,
    eventDragStart: handleEventDragStart,
    moreLinkClick: handleMoreLinkClick,
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "addClientButton dayGridMonth,customWeekView,customDayView",
    },
    buttonIcons: false,
    buttonText: {
      prev: "",
      next: "",
      today: "Today",
      month: "Month",
      week: "Week",
      day: "Day",
    },
    customButtons: {
      addClientButton: {
        text: "Add Client Filing",
        click: props.isAddClientFilingDisabled ? () => {} : props.handleAddClient,
      },
    },
    views: {
      customWeekView: {
        type: "dayGrid",
        duration: { weeks: 1 },
        events: calendarEventList,
        eventContent: renderEventContent,
        dayMaxEvents: true,
      },
      customDayView: {
        type: "dayGrid",
        duration: { days: 1 },
        events: calendarEventList,
        eventContent: renderEventContent,
        dayMaxEvents: true,
      },
    },
    eventRender: (info) => {
      const { event } = info;
      const title = event.title;

      return {
        html: `
          <div class="fc-event-title">${title}</div>
        `,
      };
    },
  };

  useEffect(() => {
    console.log("selectedeventDisplay", props?.selectedeventDisplay);
    if (props?.selectedeventDisplay) {
      setSelectedEventDisplay(props.selectedeventDisplay);
    } else {
      setSelectedEventDisplay();
    }

    if (props?.selectedEvent?.length > 0) {
      setSelectedEvent(props.selectedEvent);
    } else {
      setSelectedEvent([]);
    }
  }, [props.selectedEvent]);

  useEffect(() => {
    if (props.calendarEventList) {
      setCalendarEventList(props.calendarEventList);
    }
  }, [props.calendarEventList]);

  useEffect(() => {
    const btn = calendarContainerRef.current?.querySelector(".fc-addClientButton-button");
    if (btn) {
      if (props.isAddClientFilingDisabled) {
        btn.setAttribute("disabled", "true");
        btn.setAttribute("aria-disabled", "true");
        btn.classList.add("fc-button-disabled");
      } else {
        btn.removeAttribute("disabled");
        btn.removeAttribute("aria-disabled");
        btn.classList.remove("fc-button-disabled");
      }
    }
  }, [props.isAddClientFilingDisabled, calendarEventList]);

  return (
    <div
      id="calendar-container"
      ref={calendarContainerRef}
      className={props.isAddClientFilingDisabled ? "add-client-filing-disabled" : ""}
      style={{ width: "100%" }}
    >
      <StyledPopover>
        <FullCalendar {...calendarConfig} />
      </StyledPopover>
      <style>{`
        .fc-addClientButton-button {
          background-color: ${primaryColor} !important;
          color: white !important;
          border: none !important;
          font-size: 14px !important;    
          padding: 10px 10px !important; 
          border-radius: 24px !important;
          font-weight: 700 !important;
          font-family: 'Area Normal, Inter, sans-serif' !important;
        }
        .fc-addClientButton-button:hover {
          background-color: ${primaryColor} !important;
        }

        /* Common FullCalendar button style */
        .fc .fc-button,
        .fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button,
        .fc .fc-toolbar .fc-button-group .fc-button {
          background-color: #fff !important;
          color: ${primaryColor} !important;
          border: 1px solid ${primaryColor} !important;
          box-shadow: none !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          font-family: 'Area Normal, Inter, sans-serif' !important;
          min-width: 36px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .fc .fc-prev-button,
        .fc .fc-next-button {
          background-color: #fff !important;
          color: ${primaryColor} !important;
          border: 1px solid ${primaryColor} !important;
          box-shadow: none !important;
          min-width: 38px !important;
          width: 38px !important;
          height: 38px !important;
          padding: 0 !important;
          font-size: 0 !important;
          line-height: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .fc .fc-prev-button .fc-icon,
        .fc .fc-next-button .fc-icon {
          display: none !important;
        }

        .fc .fc-prev-button::before {
          content: "" !important;
          display: inline-block !important;
          width: 22px !important;
          height: 22px !important;
          background-color: currentColor !important;
          -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px'><path d='M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z'/></svg>") center center / contain no-repeat !important;
          mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px'><path d='M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z'/></svg>") center center / contain no-repeat !important;
        }

        .fc .fc-next-button::before {
          content: "" !important;
          display: inline-block !important;
          width: 22px !important;
          height: 22px !important;
          background-color: currentColor !important;
          -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px'><path d='M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z'/></svg>") center center / contain no-repeat !important;
          mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px'><path d='M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z'/></svg>") center center / contain no-repeat !important;
        }

        /* Hover */
        .fc .fc-button:not(:disabled):hover,
        .fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button:not(:disabled):hover,
        .fc .fc-prev-button:not(:disabled):hover,
        .fc .fc-next-button:not(:disabled):hover {
          background-color: ${primaryColor} !important;
          color: #fff !important;
          border-color: ${primaryColor} !important;
        }

        /* Active / Selected (Month, Week, Day) */
        .fc .fc-button-active,
        .fc.fc-theme-standard .fc-toolbar .fc-button-group .fc-button.fc-button-active {
          background-color: ${primaryColor} !important;
          color: #fff !important;
          border-color: ${primaryColor} !important;
        }

        /* Today button */
        .fc .fc-today-button {
          background-color: ${primaryColor} !important;
          color: #fff !important;
          border: 1px solid ${primaryColor} !important;
          font-weight: 700 !important;
          font-family: 'Area Normal, Inter, sans-serif' !important;
        }

        .fc .fc-today-button:disabled,
        .fc .fc-today-button.fc-button-disabled {
          background-color: #e5e7eb !important;
          color: #9ca3af !important;
          border: 1px solid #d1d5db !important;
          opacity: 0.8 !important;
          cursor: not-allowed !important;
        }

        /* Disabled Add Client Filing button style */
        .fc .fc-addClientButton-button:disabled,
        .fc .fc-addClientButton-button.fc-button-disabled,
        .fc button.fc-addClientButton-button:disabled,
        .add-client-filing-disabled .fc .fc-addClientButton-button,
        .add-client-filing-disabled .fc-addClientButton-button,
        .add-client-filing-disabled button.fc-addClientButton-button {
          background-color: #e5e7eb !important;
          color: #9ca3af !important;
          border: 1px solid #d1d5db !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
          opacity: 0.8 !important;
          box-shadow: none !important;
        }

        .fc .fc-addClientButton-button:disabled:hover,
        .fc .fc-addClientButton-button.fc-button-disabled:hover,
        .fc button.fc-addClientButton-button:disabled:hover,
        .add-client-filing-disabled .fc .fc-addClientButton-button:hover,
        .add-client-filing-disabled .fc-addClientButton-button:hover,
        .add-client-filing-disabled button.fc-addClientButton-button:hover {
          background-color: #e5e7eb !important;
          color: #9ca3af !important;
          border: 1px solid #d1d5db !important;
        }

        .fc .fc-col-header-cell-cushion,
        .fc .fc-daygrid-day-number {
          color: ${primaryColor} !important;
        }
      `}</style>
    </div>
  );
};

export default Calendar;
